<template>
  <div class="carousel" ref="carousel">
    <div
      v-for="i in slotCount"
      :key="i"
      class="carousel-item"
      :class="carouselItemClass(i - 1)"
    >
      <div class="mask" v-if="isRendered(i - 1)">
        <div class="slot slot-left">
          <slot :name="`left-${i}`" />
        </div>
        <div class="slot slot-right">
          <slot :name="`right-${i}`" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  let prevOnce = true;
  export default {
    props: {
      slotCount: {
        type: Number,
        default: 3,
        required: false,
        validator: value => {
          return value > 2 && value % 1 === 0;
        }
      },
      interval: {
        type: Number,
        default: 3000,
        required: false,
        validator: value => {
          return value > 0;
        }
      }
    },
    data() {
      return {
        activeSlot: 0,
        mouseScrollEvent: 'mousewheel',
        intervalId: null,
        isActiveChangeAnimation: false,
        clearSwiperLeftCallback: () => {},
        getTime: 0
      };
    },
    mounted() {
      this.startTransition();

      window.addEventListener('keyup', this.keyupHandler);

      this.setMouseScrollEvent();
      window.addEventListener(this.mouseScrollEvent, this.mousewheelHandler);

      this.clearSwiperLeftCallback = this.setSwipeLeft();
    },
    beforeDestroy() {
      this.stopTransition();

      window.removeEventListener('keyup', this.keyupHandler);
      window.removeEventListener(this.mouseScrollEvent, this.mousewheelHandler);

      this.clearSwiperLeftCallback();
    },
    methods: {
      carouselItemClass(i) {
        if (i === this.activeSlot) {
          return 'active';
        } else if (i === this.nextSlot) {
          return 'next';
        } else if (i === this.prevSlot) {
          if (prevOnce) {
            prevOnce = false;
            return '';
          }
          return 'prev';
        } else {
          return '';
        }
      },
      isRendered(i) {
        return (
          i === this.activeSlot || i === this.nextSlot || i === this.prevSlot
        );
      },
      // If you want to enable automatic scrolling
      startTransition(slotIndex) {
        if (typeof slotIndex === 'undefined' || slotIndex === this.activeSlot) {
          // this.intervalId = setInterval(this.changeSlot, this.interval);
        }
      },
      stopTransition() {
        clearInterval(this.intervalId);
      },
      changeSlot() {
        this.activeSlot = this.nextSlot;
        this.$emit('change', this.activeSlot);
      },
      forceChangeSlot(i) {
        if (i === this.activeSlot || this.isActiveChangeAnimation) {
          return;
        }

        const animationTime = 700;
        this.stopTransition();

        this.isActiveChangeAnimation = true;
        const finishAnimation = () => {
          setTimeout(() => {
            this.isActiveChangeAnimation = false;
          }, animationTime);
        };

        if (typeof i === 'undefined' || i === this.nextSlot) {
          this.changeSlot();
          finishAnimation();

          this.startTransition();
          return null;
        }

        const intervalId = setInterval(() => {
          this.changeSlot();

          if (i === this.activeSlot) {
            finishAnimation();
            
            clearInterval(intervalId);
            this.startTransition();
            return null;
          }
        }, animationTime);
      },
      keyupHandler(e) {
        if (e.key === 'ArrowRight' && !this.isActiveChangeAnimation) {
          this.forceChangeSlot();
        }
      },
      setMouseScrollEvent() {
        if ('onwheel' in window.document) {
          this.mouseScrollEvent = 'wheel';
        } else if ('onmousewheel' in document) {
          this.mouseScrollEvent = 'mousewheel';
        } else {
          this.mouseScrollEvent = 'MozMousePixelScroll';
        }
      },
      mousewheelHandler(e) {
        e = e || window.event;
        const delta = e.deltaY || e.detail || e.wheelDelta;

        if (delta >= 0 && !this.isActiveChangeAnimation) {
          this.forceChangeSlot();
        }
      },
      setSwipeLeft() {
        let i = 0;
        let x0 = null;

        const unify = e => (e.changedTouches ? e.changedTouches[0] : e);
        const lock = e => {
          x0 = unify(e).clientX;
        };

        const move = e => {
          if (x0 || x0 === 0) {
            let dx = unify(e).clientX - x0;
            let s = Math.sign(dx);

            if ((i > 0 || s < 0) && !this.isActiveChangeAnimation) {
              this.forceChangeSlot();
            }
            x0 = null;
          }
        };

        const crs = this.$refs.carousel;
        crs.addEventListener('mousedown', lock, false);
        crs.addEventListener('touchstart', lock, false);

        //const prevent = e => e.preventDefault();
        //crs.addEventListener('touchmove', prevent, false);

        crs.addEventListener('mouseup', move, false);
        crs.addEventListener('touchend', move, false);

        return () => {
          crs.removeEventListener('mousedown', lock);
          crs.removeEventListener('touchstart', lock);
          //crs.removeEventListener('touchmove', prevent);
          crs.removeEventListener('mouseup', move);
          crs.removeEventListener('touchend', move);
        };
      }
    },
    computed: {
      nextSlot() {
        return this.activeSlot < this.slotCount - 1 ? this.activeSlot + 1 : 0;
      },
      prevSlot() {
        return this.activeSlot > 0 ? this.activeSlot - 1 : this.slotCount - 1;
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import '~styles/scss/media.scss';
  .carousel,
  .carousel-item {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .carousel {
    user-select: none;
    &-item {
      .mask {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transition: opacity 0.3s ease-in-out;
      }

      .slot {
        position: absolute;
        top: 0;
        height: 100%;
        width: 50%;
        &-left {
          left: 0;
        }
        &-right {
          right: 0;
        }

        @include respond-to(tabletRotate) {
          width: 100%;
        }
      }

      &.prev {
        .slot {
          &-left {
            animation: prev-slot-left 0.5s ease-in-out both;
          }
          &-right {
            animation: prev-slot-right 0.5s ease-in-out both;
          }

          @include respond-to(tabletRotate) {
            &-left,
            &-right {
              animation: none;
            }
          }
        }
        @include respond-to(tabletRotate) {
          animation: prev-item-tablet-r 0.5s ease-in-out forwards;
        }
      }
      &.active {
        animation: active-item 0.8s ease-in-out;
        z-index: 15;
        @include respond-to(tabletRotate) {
          animation: active-item-tablet-r 0.5s ease-in-out backwards;
        }
      }
      &.next {
        animation: next-item 1s ease-in-out both;
        .mask {
          opacity: 0.3;
          -webkit-filter: blur(11px);
          filter: blur(11px);
        }
        @include respond-to(tabletRotate) {
          animation: none;
          opacity: 0;
        }
      }

      visibility: hidden;
      &.prev,
      &.active,
      &.next {
        visibility: visible;
      }
    }
  }

  @keyframes prev-slot-left {
    from {
      z-index: 20;
    }
    99% {
      z-index: -1;
      opacity: 1;
    }
    to {
      transform: translateX(-100%);
      opacity: 0;
    }
  }
  @keyframes prev-slot-right {
    from {
      z-index: 20;
    }
    99% {
      z-index: -1;
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }

  @keyframes active-item {
    from {
      transform: scale(0.7) translateX(3%);
    }
    60% {
      transform: scale(0.9) translate(-5%, 0);
    }
    to {
      transform: none;
    }
  }

  @keyframes next-item {
    from {
      transform: scale(0);
      transform-origin: 60% 50%;
    }
    80% {
      transform: scale(0.7) translateX(3%);
    }
    to {
      transform-origin: center;
      transform: scale(0.7) translateX(3%);
    }
  }

  @keyframes prev-item-tablet-r {
    from {
    }
    40% {
      opacity: 1;
    }
    to {
      transform: translateX(-100%);
      opacity: 0;
    }
  }
  @keyframes active-item-tablet-r {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    40% {
      opacity: 1;
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  .carousel {
    @include respond-to(tabletRotate) {
      position: relative;
      height: auto;
      &-item,
      .mask,
      .slot {
        position: relative;
        height: auto;
      }
      &-item {
        position: absolute;
        top: 0;
        margin-right: 0;
        height: 100%;
        &.active {
          position: relative;
          height: auto;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Carousel.vue